/**
 * Mid / Bid / Ask, one tap each.
 *
 * Every price here comes from `bookRefPrices`, which has ALREADY put the mid on
 * the market's grid the right way round for the side being traded. Nothing in
 * this file rounds, pads or reformats: a chip hands back exactly the string it
 * was given, because a price the trader can see and a price the order carries
 * must be the same price.
 *
 * A side the book does not quote is `aria-disabled` rather than `disabled` — it
 * stays focusable so a screen reader can reach it and read why it is off,
 * instead of the chip silently vanishing from the tab order as depth comes and
 * goes.
 */
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TEXT_COLORS } from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { SPACING } from "../../../constants/design/spacing";
import { bookRefPrices } from "../../../utils/amount/price";
import { ActionRow, Chip, FieldWrapper, HintText, monoFigures } from "./style";

export type PriceRefKind = "mid" | "bid" | "ask";

export interface PriceChipsProps {
  refs: ReturnType<typeof bookRefPrices>;
  onPick: (kind: PriceRefKind, price: string) => void;
  disabled?: boolean;
}

const KINDS: PriceRefKind[] = ["mid", "bid", "ask"];

const ChipContent = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${SPACING.one}px;
  justify-content: center;
  width: 100%;
`;

const ChipPrice = styled.span`
  ${monoFigures};
  font-size: ${FONT_SIZE.xs};
  color: ${TEXT_COLORS.three};
  overflow: hidden;
  text-overflow: ellipsis;

  /* Label only once the row is too narrow to hold both: the label is what the
     chip does, the price is a convenience. */
  @media (max-width: 420px) {
    display: none;
  }
`;

export function PriceChips({ refs, onPick, disabled }: PriceChipsProps) {
  const { t } = useTranslation("app", { keyPrefix: "common.inputs.price" });

  // `none` means neither the book nor the mark could price this market. There
  // is nothing to fill with, so no chip is live — not even a side that happens
  // to have depth, because a one-sided book with no mark is not a market.
  const priced = refs.source !== "none" && !disabled;

  const priceFor = (kind: PriceRefKind): string | undefined =>
    kind === "mid" ? refs.mid : kind === "bid" ? refs.bid : refs.ask;

  return (
    <FieldWrapper>
      <ActionRow>
        {KINDS.map((kind) => {
          const price = priceFor(kind);
          const off = !priced || !price;
          return (
            <Chip
              key={kind}
              type="button"
              aria-disabled={off || undefined}
              title={price}
              onClick={() => {
                if (off || !price) return;
                onPick(kind, price);
              }}
            >
              <ChipContent>
                <span>{t(kind)}</span>
                {price ? <ChipPrice>{price}</ChipPrice> : null}
              </ChipContent>
            </Chip>
          );
        })}
      </ActionRow>
      {refs.source === "mark" ? <HintText>{t("mark_hint")}</HintText> : null}
      {refs.source === "none" ? <HintText>{t("none_hint")}</HintText> : null}
    </FieldWrapper>
  );
}
